import { FetchResult } from '@apollo/client'
import {
  Avatar,
  Box,
  Button,
  Divider,
  FormHelperText,
  Link,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { Fragment, useMemo } from 'react'
import { CautionText, Icons, SkeletonText } from 'utoniq-ui'

import {
  Collection,
  Maybe,
  StoreCardDetailFragment,
  UserPaymentType,
} from '@/clients/utoniq-core/schema'
import { BaseLayout } from '@/components/layouts'
import { ContentFrame } from '@/components/layouts/ContentFrame'
import { QuantityPicker } from '@/components/organisms'
import { styleSets } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { individualDisplayResponsive } from '@/core/individual'
import { setRedirectAfterLoginPath } from '@/core/native/storage'

export * from './fragments'

export type State = {
  loading: boolean
  isAnonymous: boolean
  isSubmitting: boolean
  changingQualityCart: boolean
  cart: StoreCardDetailFragment | null
  cartItems: StoreCardDetailFragment['items']
  cartItemsByCollection:
    | {
        collection: Maybe<Pick<Collection, 'id' | 'title'>> | undefined
        items: StoreCardDetailFragment['items']
      }[]
    | null
  personalInformationRequirements: string[]
  errors?: {
    itemId: string
    skuId?: string
    errorCode: string
    overNumber: number
    purchaseNumber: number
    stockLimit: number
    userMessage: string
  }[]
  addToCartErrors: AddToCartError[] | undefined
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onAddToCart: (
    itemId: string,
    skuId?: string
  ) => Promise<FetchResult | number | void | undefined>
  onRemoveFromCart: (
    itemId: string,
    count: number,
    skuId: string | undefined,
    removeAll?: boolean
  ) => Promise<number | void | undefined>
  onValidateCart: () => Promise<void>
}

export type AddToCartError = { itemId: string; userMessage: string }

export const StoreCartDetailTemplate: Template<State, Action> = ({
  state,
  onAddToCart,
  onRemoveFromCart,
  onValidateCart,
}) => {
  const { t } = useTranslation('storeCartDetail')
  const { routerPages, router, generatePath } = useRouter()

  const displayResponsive = individualDisplayResponsive()

  return (
    <BaseLayout
      header={{
        back: true,
        defaultBackPage: routerPages.StoreCartList,
        title: state.cart?.artist.displayName
          ? state.cart?.artist.displayName + t('title')
          : '',
        ...(displayResponsive
          ? {
              responsive: true,
              maxWidth: 1120,
            }
          : {}),
      }}
      customNavigation={
        <FixedBottomCell
          loading={state.loading}
          isSubmitting={state.isSubmitting}
          onValidateCart={onValidateCart}
          isAnonymous={state.isAnonymous}
          personalInformationRequirements={
            state.personalInformationRequirements
          }
        />
      }
      {...(displayResponsive
        ? {
            customNavigationSx: {
              maxWidth: '100%',
            },
            responsive: true,
          }
        : {})}
    >
      <StoreInfoCell
        loading={state.loading}
        artist={state.cart?.artist ?? null}
      />
      <OrderDetailCell
        loading={state.loading}
        changingQualityCart={state.changingQualityCart}
        cartItems={state.cartItems}
        formattedTotalPrice={state.cart?.formattedTotalPrice ?? '0'}
        cartItemsByCollection={state.cartItemsByCollection}
        errors={state.errors}
        onAddToCart={onAddToCart}
        addToCartErrors={state.addToCartErrors}
        onRemoveFromCart={onRemoveFromCart}
      />
      {!state.loading && (
        <Stack sx={{ px: 5, pb: 5 }}>
          <ContentFrame>
            <CautionText type="normal">
              <Typography variant="caption" color="text.scondary">
                {t('buyItemNote')}
              </Typography>
            </CautionText>
          </ContentFrame>
        </Stack>
      )}
    </BaseLayout>
  )
}

type StoreInfoCellProps = {
  loading: boolean
  artist: StoreCardDetailFragment['artist'] | null
}
export const StoreInfoCell: Cell<StoreInfoCellProps> = ({
  loading,
  artist,
}) => {
  const { t } = useTranslation('storeCartDetail')
  if (loading || !artist) {
    return <StoreInfoSkeleton />
  }
  return (
    <CellFrame sx={{ pb: 4 }}>
      <Stack sx={{ bgcolor: 'grey.50', px: 5, py: 2 }}>
        <ContentFrame>
          <Typography variant="h5" color="text.secondary">
            {t('storeInfo')}
          </Typography>
        </ContentFrame>
      </Stack>
      <ContentFrame>
        {!artist ? (
          <EmptyStateCell message={t('storeInfoEmptyMessage')} />
        ) : (
          <Stack sx={{ px: 5, py: 3 }}>
            <Stack direction="row" spacing={3} alignItems="center">
              <Avatar
                src={artist?.avatar?.smallUri ?? ''}
                sx={{ width: 40, height: 40 }}
              />
              <Stack spacing={1}>
                <Typography variant="subtitle1">
                  {artist.displayName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {artist.introduction}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        )}
      </ContentFrame>
    </CellFrame>
  )
}

const StoreInfoSkeleton: Cell = () => (
  <CellFrame sx={{ pb: 5 }}>
    <Box sx={{ px: 5, py: 2, bgcolor: 'grey.50' }}>
      <SkeletonText variant="h3" width="50%" />
    </Box>
    <ContentFrame>
      <Stack
        direction="row"
        spacing={3}
        alignItems="center"
        sx={{ px: 5, py: 3 }}
      >
        <Skeleton variant="circular" sx={{ width: 40, height: 40 }} />
        <Stack flex={1}>
          <SkeletonText variant="small" width="75%" />
          <Stack sx={{ pt: 2 }}>
            <SkeletonText variant="small" width="85%" />
            <SkeletonText variant="small" width="85%" />
          </Stack>
        </Stack>
      </Stack>
    </ContentFrame>
  </CellFrame>
)

type OrderDetailCellProps = {
  loading: boolean
  changingQualityCart: boolean
  errors: State['errors']
  formattedTotalPrice: string
  addToCartErrors: State['addToCartErrors']
  cartItems?: StoreCardDetailFragment['items']
  cartItemsByCollection: State['cartItemsByCollection']
  onAddToCart: Action['onAddToCart']
  onRemoveFromCart: Action['onRemoveFromCart']
}
const OrderDetailCell: Cell<OrderDetailCellProps> = ({
  loading,
  changingQualityCart,
  formattedTotalPrice,
  errors,
  cartItems,
  onAddToCart,
  onRemoveFromCart,
  cartItemsByCollection,
  addToCartErrors,
}) => {
  const { t } = useTranslation('storeCartDetail')
  const paymentType = useMemo(() => {
    if (
      !cartItemsByCollection?.length ||
      !cartItemsByCollection[0]?.items?.length ||
      !cartItemsByCollection[0]?.items[0]?.item?.paymentType
    )
      return UserPaymentType.PrepaidPoint
    return cartItemsByCollection[0].items[0].item.paymentType
  }, [cartItemsByCollection])

  if (loading) {
    return <OrderDetailSkeleton />
  }

  return (
    <CellFrame sx={{ pb: 4 }}>
      <Stack sx={{ bgcolor: 'grey.50', px: 5, py: 2 }}>
        <ContentFrame>
          <Typography variant="h5" color="text.secondary">
            {t('orderDetail')}
          </Typography>
        </ContentFrame>
      </Stack>
      <ContentFrame>
        {!cartItems?.length ? (
          <EmptyStateCell message={t('orderDetailEmptyMessage')} />
        ) : (
          <Stack sx={{ px: 5, py: 3 }}>
            <Stack spacing={5} sx={{ mt: 1 }}>
              {cartItemsByCollection?.map(({ collection, items }) => {
                return (
                  <Stack spacing={4} key={collection?.id}>
                    {collection && (
                      <Typography variant="h6">{collection?.title}</Typography>
                    )}
                    {items.map(cartItem => {
                      const addToCartError = addToCartErrors?.find(
                        ({ itemId }) => itemId === cartItem.item.id
                      )

                      return (
                        <Fragment
                          key={cartItem.item.id + (cartItem.item.sku?.id ?? '')}
                        >
                          <OrderItemCell
                            userCartItem={cartItem}
                            errors={errors}
                            changingQualityCart={changingQualityCart}
                            paymentType={paymentType}
                            onPlus={async () => {
                              onAddToCart(
                                cartItem.item.id,
                                cartItem.item.sku?.id
                              )
                            }}
                            onMinus={async () => {
                              onRemoveFromCart(
                                cartItem.item.id,
                                1,
                                cartItem.item.sku?.id
                              )
                            }}
                            onDelete={async () => {
                              onRemoveFromCart(
                                cartItem.item.id,
                                cartItem.count,
                                cartItem.item.sku?.id,
                                true
                              )
                            }}
                            addToCartErrorMessage={addToCartError?.userMessage}
                          />
                          <Divider />
                        </Fragment>
                      )
                    })}
                  </Stack>
                )
              })}
            </Stack>
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ pt: 2 }}
            >
              <Typography variant="h6">{t('paymentAmount')}</Typography>
              <Stack direction="row" alignItems="center" gap={1}>
                {paymentType === UserPaymentType.PrepaidPoint && (
                  <Icons.Point fontSize="small" />
                )}
                <Typography variant="h6">
                  {paymentType === UserPaymentType.Cash && '¥'}
                  {formattedTotalPrice}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        )}
      </ContentFrame>
    </CellFrame>
  )
}

type OrderItemCellProps = {
  userCartItem: StoreCardDetailFragment['items'][number]
  errors: State['errors']
  changingQualityCart: boolean
  paymentType: UserPaymentType
  onPlus: () => Promise<void>
  onMinus: () => Promise<void>
  onDelete: () => Promise<void>
  addToCartErrorMessage: string | undefined
}
const OrderItemCell: Cell<OrderItemCellProps> = ({
  userCartItem,
  errors,
  changingQualityCart,
  paymentType,
  onPlus,
  onMinus,
  onDelete,
  addToCartErrorMessage,
}) => {
  const { t } = useTranslation('storeCartDetail')
  const { generatePath, routerPages } = useRouter()
  const error = useMemo(() => {
    if (!errors?.length) {
      return
    }
    return errors.find(
      er =>
        er?.skuId === userCartItem.item.sku?.id &&
        er.itemId === userCartItem.item.id
    )
  }, [errors, userCartItem.item.id, userCartItem.item.sku?.id])

  const formattedPrice = useMemo(() => {
    if (userCartItem.item.sku?.price) {
      return userCartItem.item.sku?.formattedPrice
    }
    return userCartItem.item.formattedPrice
  }, [
    userCartItem.item.formattedPrice,
    userCartItem.item.sku?.formattedPrice,
    userCartItem.item.sku?.price,
  ])

  if (!userCartItem.item.issuedArtist) return null

  const skuUnitName = userCartItem.item.sku?.unitName ?? null
  const storeLink = generatePath(routerPages.ArtistStoreItemDetail, {
    itemId: userCartItem.item.id,
    artistId: userCartItem.item.issuedArtist.id,
  })

  return (
    <CellFrame>
      <Stack direction="row" alignItems="center" spacing={3}>
        <Link href={storeLink}>
          <Avatar
            variant="square"
            src={userCartItem.item.thumbnail.front?.uri}
            sx={{
              width: 60,
              height: 96,
              '& > img': { objectFit: 'contain' },
            }}
          />
        </Link>
        <Stack flex={1} sx={{ minWidth: 0, alignSelf: 'flex-start' }}>
          <Link
            href={storeLink}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: 'fit-content',
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              gap={4}
              sx={{
                pt: !userCartItem.item.sku?.unitName ? 2 : 0,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  ...{ ...(styleSets.lineCamp(1) as {}) },
                  whiteSpace: 'pre-wrap',
                }}
              >
                {userCartItem.item.title}
              </Typography>
              <Icons.RightArrow fontSize="xSmall" sx={{ mb: '3px' }} />
            </Stack>
            {!!skuUnitName && (
              <Typography variant="caption">{skuUnitName}</Typography>
            )}
            <Stack direction="row" alignItems="center" gap={1}>
              {paymentType === UserPaymentType.PrepaidPoint && (
                <Icons.Point sx={{ fontSize: 16 }} />
              )}
              <Typography variant="subtitle1" sx={{ pt: 1 }}>
                {paymentType === UserPaymentType.Cash && '¥'}
                {formattedPrice}
              </Typography>
            </Stack>
          </Link>
          <Stack direction="row-reverse" sx={{ pt: 1 }}>
            <Stack alignItems="flex-end">
              <Stack direction="row" alignItems="center" spacing={4}>
                <Button
                  onClick={onDelete}
                  disabled={changingQualityCart}
                  variant="text"
                  sx={{
                    typography: 'overline',
                    color: 'text.secondary',
                  }}
                >
                  {t('delete')}
                </Button>
                <QuantityPicker
                  value={userCartItem.count}
                  onPlus={onPlus}
                  onMinus={onMinus}
                  error={!!error}
                />
              </Stack>
              {(!!error || !!addToCartErrorMessage) && (
                <FormHelperText error sx={{ bgcolor: 'transparent' }}>
                  {addToCartErrorMessage
                    ? addToCartErrorMessage
                    : error?.userMessage}
                </FormHelperText>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </CellFrame>
  )
}

const OrderDetailSkeleton: Cell = () => (
  <CellFrame sx={{ pb: 5 }}>
    <Box sx={{ py: 2, px: 5, bgcolor: 'grey.50' }}>
      <SkeletonText variant="h3" width="50%" />
    </Box>
    <ContentFrame>
      <Stack sx={{ p: 5, py: 3 }}>
        <Stack direction="row" alignItems="center" spacing={3} sx={{ pb: 5 }}>
          <Skeleton variant="rectangular" sx={{ width: 60, height: 96 }} />
          <Stack flex={1}>
            <SkeletonText variant="large" width="100%" />
            <SkeletonText variant="small" width="100%" />
            <Stack direction="row-reverse" sx={{ pt: 4 }}>
              <SkeletonText variant="large" width="40%" />
            </Stack>
          </Stack>
        </Stack>
        <Divider />
        <Stack direction="row" justifyContent="space-between" sx={{ pt: 2 }}>
          <SkeletonText variant="large" width="30%" />
          <SkeletonText variant="large" width="30%" />
        </Stack>
      </Stack>
    </ContentFrame>
  </CellFrame>
)

type EmptyStateCellProps = {
  message: string
}
const EmptyStateCell: Cell<EmptyStateCellProps> = ({ message }) => (
  <CellFrame sx={{ py: 6 }}>
    <Typography variant="body1" color="text.secondary" align="center">
      {message}
    </Typography>
  </CellFrame>
)

type FixedBottomCellProps = {
  loading: boolean
  isSubmitting: boolean
  onValidateCart: Action['onValidateCart']
  isAnonymous: boolean
  personalInformationRequirements: string[]
}
const FixedBottomCell: Cell<FixedBottomCellProps> = ({
  loading,
  isSubmitting,
  onValidateCart,
  isAnonymous,
  personalInformationRequirements,
}) => {
  const { t } = useTranslation('storeCartDetail')
  const { routerPages, router } = useRouter()

  return (
    <CellFrame sx={{ py: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
      {loading ? (
        <Skeleton
          variant="rectangular"
          sx={{ width: 244, height: 44, borderRadius: 34, mx: 'auto' }}
        />
      ) : isAnonymous ? (
        <Stack sx={{ gap: 2, maxWidth: '244px', m: 'auto' }}>
          <Button
            size="large"
            sx={{ minWidth: 244 }}
            onClick={async () => {
              await setRedirectAfterLoginPath(
                personalInformationRequirements.length
                  ? routerPages.StoreUserInfo
                  : router.pathname
              )
              router.push(routerPages.SignUp)
            }}
          >
            {t('signUp', '新規登録して購入手続きへ')}
          </Button>
          <Button
            size="large"
            sx={{ minWidth: 244 }}
            variant="text"
            href={`${routerPages.Login}?redirectUri=${encodeURIComponent(
              personalInformationRequirements.length
                ? routerPages.StoreUserInfo
                : router.pathname
            )}`}
          >
            {t('login', 'ログインして購入手続きへ')}
          </Button>
        </Stack>
      ) : (
        <Button
          size="large"
          sx={{ minWidth: 244 }}
          loading={isSubmitting}
          onClick={onValidateCart}
        >
          {t('paymentMethodSetting')}
        </Button>
      )}
    </CellFrame>
  )
}
